import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import userService from '../services/userService';

// ───────────────────────── Async Thunks ─────────────────────────

// List users (paged)
export const fetchAdminUsers = createAsyncThunk(
  'adminUsers/fetchAdminUsers',
  async (pageable = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getAdminUsers(pageable);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get user by ID
export const fetchAdminUserById = createAsyncThunk(
  'adminUsers/fetchAdminUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getAdminUserById(userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Enable user
export const enableUser = createAsyncThunk(
  'adminUsers/enableUser',
  async (userId, { rejectWithValue }) => {
    try {
      await userService.enableUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Disable user
export const disableUser = createAsyncThunk(
  'adminUsers/disableUser',
  async (userId, { rejectWithValue }) => {
    try {
      await userService.disableUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add role to user
export const addUserRole = createAsyncThunk(
  'adminUsers/addUserRole',
  async ({ userId, roleName }, { rejectWithValue }) => {
    try {
      await userService.addUserRole(userId, roleName);
      return { userId, roleName };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Remove role from user
export const removeUserRole = createAsyncThunk(
  'adminUsers/removeUserRole',
  async ({ userId, roleName }, { rejectWithValue }) => {
    try {
      await userService.removeUserRole(userId, roleName);
      return { userId, roleName };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete user
export const deleteUser = createAsyncThunk(
  'adminUsers/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await userService.deleteUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get user video purchases (paged)
export const fetchUserVideoPurchases = createAsyncThunk(
  'adminUsers/fetchUserVideoPurchases',
  async (pageable = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getUserVideoPurchases(pageable);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get user's last 10 purchases after influencer code usage
export const fetchUserLast10PurchasesAfterInfluencerCodeUsage = createAsyncThunk(
  'adminUsers/fetchUserLast10PurchasesAfterInfluencerCodeUsage',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserLast10PurchasesAfterInfluencerCodeUsage(userId);
      return { userId, purchases: response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Export users purchases to Excel
export const exportUsersPurchasesToExcel = createAsyncThunk(
  'adminUsers/exportUsersPurchasesToExcel',
  async ({ months, year }, { rejectWithValue }) => {
    try {
      const response = await userService.exportUsersPurchasesToExcel(months, year);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Get user footage purchase hours (paged)
export const fetchUserFootagePurchaseHours = createAsyncThunk(
  'adminUsers/fetchUserFootagePurchaseHours',
  async (pageable = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getUserFootagePurchaseHours(pageable);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ───────────────────────── Initial State ─────────────────────────

const initialState = {
  // Users data
  users: [],
  usersLoading: false,
  usersError: null,
  
  // Pagination
  usersPage: 0,
  usersRowsPerPage: 10,
  usersTotalElements: 0,
  usersTotalPages: 0,
  
  // Selected user details
  selectedUser: null,
  selectedUserLoading: false,
  selectedUserError: null,
  
  // User actions
  actionLoading: false,
  actionError: null,
  
  // Video purchases
  videoPurchases: [],
  videoPurchasesLoading: false,
  videoPurchasesError: null,
  videoPurchasesPage: 0,
  videoPurchasesRowsPerPage: 10,
  videoPurchasesTotalElements: 0,
  videoPurchasesTotalPages: 0,
  
  // Influencer purchases
  influencerPurchases: {},
  influencerPurchasesLoading: false,
  influencerPurchasesError: null,
  
  // Footage hours
  footageHours: [],
  footageHoursLoading: false,
  footageHoursError: null,
  footageHoursPage: 0,
  footageHoursRowsPerPage: 10,
  footageHoursTotalElements: 0,
  footageHoursTotalPages: 0,
  
  // Export
  exportLoading: false,
  exportError: null
};

// ───────────────────────── Slice ─────────────────────────

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    // Clear errors
    clearErrors: (state) => {
      state.usersError = null;
      state.selectedUserError = null;
      state.actionError = null;
      state.videoPurchasesError = null;
      state.influencerPurchasesError = null;
      state.footageHoursError = null;
      state.exportError = null;
    },
    
    // Clear selected user
    clearSelectedUser: (state) => {
      state.selectedUser = null;
      state.selectedUserError = null;
    },
    
    // Update pagination
    setUsersPage: (state, action) => {
      state.usersPage = action.payload;
    },
    
    setUsersRowsPerPage: (state, action) => {
      state.usersRowsPerPage = action.payload;
      state.usersPage = 0; // Reset to first page
    },
    
    setVideoPurchasesPage: (state, action) => {
      state.videoPurchasesPage = action.payload;
    },
    
    setVideoPurchasesRowsPerPage: (state, action) => {
      state.videoPurchasesRowsPerPage = action.payload;
      state.videoPurchasesPage = 0; // Reset to first page
    },
    
    setFootageHoursPage: (state, action) => {
      state.footageHoursPage = action.payload;
    },
    
    setFootageHoursRowsPerPage: (state, action) => {
      state.footageHoursRowsPerPage = action.payload;
      state.footageHoursPage = 0; // Reset to first page
    }
  },
  extraReducers: (builder) => {
    // ───────────────────────── Fetch Admin Users ─────────────────────────
    builder
      .addCase(fetchAdminUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        
        // Handle paginated response
        if (action.payload.content) {
          state.users = action.payload.content;
          state.usersTotalElements = action.payload.totalElements;
          state.usersTotalPages = action.payload.totalPages;
        } else {
          // Fallback for non-paginated response
          state.users = Array.isArray(action.payload) ? action.payload : [];
          state.usersTotalElements = Array.isArray(action.payload) ? action.payload.length : 0;
          state.usersTotalPages = 1;
        }
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
      });

    // ───────────────────────── Fetch Admin User By ID ─────────────────────────
    builder
      .addCase(fetchAdminUserById.pending, (state) => {
        state.selectedUserLoading = true;
        state.selectedUserError = null;
      })
      .addCase(fetchAdminUserById.fulfilled, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchAdminUserById.rejected, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUserError = action.payload;
      });

    // ───────────────────────── Enable User ─────────────────────────
    builder
      .addCase(enableUser.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(enableUser.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Update user status in the list
        const user = state.users.find(u => u.id === action.payload);
        if (user) {
          user.status = 'ENABLED';
        }
        // Update selected user if it's the same
        if (state.selectedUser && state.selectedUser.id === action.payload) {
          state.selectedUser.status = 'ENABLED';
        }
      })
      .addCase(enableUser.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // ───────────────────────── Disable User ─────────────────────────
    builder
      .addCase(disableUser.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(disableUser.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Update user status in the list
        const user = state.users.find(u => u.id === action.payload);
        if (user) {
          user.status = 'DISABLED';
        }
        // Update selected user if it's the same
        if (state.selectedUser && state.selectedUser.id === action.payload) {
          state.selectedUser.status = 'DISABLED';
        }
      })
      .addCase(disableUser.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // ───────────────────────── Add User Role ─────────────────────────
    builder
      .addCase(addUserRole.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(addUserRole.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { userId, roleName } = action.payload;
        
        // Update user roles in the list
        const user = state.users.find(u => u.id === userId);
        if (user && user.roles && !user.roles.includes(roleName)) {
          user.roles.push(roleName);
        }
        
        // Update selected user if it's the same
        if (state.selectedUser && state.selectedUser.id === userId) {
          if (!state.selectedUser.roles.includes(roleName)) {
            state.selectedUser.roles.push(roleName);
          }
        }
      })
      .addCase(addUserRole.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // ───────────────────────── Remove User Role ─────────────────────────
    builder
      .addCase(removeUserRole.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(removeUserRole.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { userId, roleName } = action.payload;
        
        // Update user roles in the list
        const user = state.users.find(u => u.id === userId);
        if (user && user.roles) {
          user.roles = user.roles.filter(role => role !== roleName);
        }
        
        // Update selected user if it's the same
        if (state.selectedUser && state.selectedUser.id === userId) {
          state.selectedUser.roles = state.selectedUser.roles.filter(role => role !== roleName);
        }
      })
      .addCase(removeUserRole.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // ───────────────────────── Delete User ─────────────────────────
    builder
      .addCase(deleteUser.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Remove user from the list
        state.users = state.users.filter(u => u.id !== action.payload);
        state.usersTotalElements = Math.max(0, state.usersTotalElements - 1);
        
        // Clear selected user if it's the deleted one
        if (state.selectedUser && state.selectedUser.id === action.payload) {
          state.selectedUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // ───────────────────────── Fetch User Video Purchases ─────────────────────────
    builder
      .addCase(fetchUserVideoPurchases.pending, (state) => {
        state.videoPurchasesLoading = true;
        state.videoPurchasesError = null;
      })
      .addCase(fetchUserVideoPurchases.fulfilled, (state, action) => {
        state.videoPurchasesLoading = false;
        
        // Handle paginated response
        if (action.payload.content) {
          state.videoPurchases = action.payload.content;
          state.videoPurchasesTotalElements = action.payload.totalElements;
          state.videoPurchasesTotalPages = action.payload.totalPages;
        } else {
          // Fallback for non-paginated response
          state.videoPurchases = Array.isArray(action.payload) ? action.payload : [];
          state.videoPurchasesTotalElements = Array.isArray(action.payload) ? action.payload.length : 0;
          state.videoPurchasesTotalPages = 1;
        }
      })
      .addCase(fetchUserVideoPurchases.rejected, (state, action) => {
        state.videoPurchasesLoading = false;
        state.videoPurchasesError = action.payload;
      });

    // ───────────────────────── Fetch User Last 10 Purchases After Influencer Code Usage ─────────────────────────
    builder
      .addCase(fetchUserLast10PurchasesAfterInfluencerCodeUsage.pending, (state) => {
        state.influencerPurchasesLoading = true;
        state.influencerPurchasesError = null;
      })
      .addCase(fetchUserLast10PurchasesAfterInfluencerCodeUsage.fulfilled, (state, action) => {
        state.influencerPurchasesLoading = false;
        const { userId, purchases } = action.payload;
        state.influencerPurchases[userId] = purchases;
      })
      .addCase(fetchUserLast10PurchasesAfterInfluencerCodeUsage.rejected, (state, action) => {
        state.influencerPurchasesLoading = false;
        state.influencerPurchasesError = action.payload;
      });

    // ───────────────────────── Export Users Purchases To Excel ─────────────────────────
    builder
      .addCase(exportUsersPurchasesToExcel.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportUsersPurchasesToExcel.fulfilled, (state) => {
        state.exportLoading = false;
        // Export handled in component
      })
      .addCase(exportUsersPurchasesToExcel.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload;
      });

    // ───────────────────────── Fetch User Footage Purchase Hours ─────────────────────────
    builder
      .addCase(fetchUserFootagePurchaseHours.pending, (state) => {
        state.footageHoursLoading = true;
        state.footageHoursError = null;
      })
      .addCase(fetchUserFootagePurchaseHours.fulfilled, (state, action) => {
        state.footageHoursLoading = false;
        
        // Handle paginated response
        if (action.payload.content) {
          state.footageHours = action.payload.content;
          state.footageHoursTotalElements = action.payload.totalElements;
          state.footageHoursTotalPages = action.payload.totalPages;
        } else {
          // Fallback for non-paginated response
          state.footageHours = Array.isArray(action.payload) ? action.payload : [];
          state.footageHoursTotalElements = Array.isArray(action.payload) ? action.payload.length : 0;
          state.footageHoursTotalPages = 1;
        }
      })
      .addCase(fetchUserFootagePurchaseHours.rejected, (state, action) => {
        state.footageHoursLoading = false;
        state.footageHoursError = action.payload;
      });
  }
});

// ───────────────────────── Exports ─────────────────────────

export const {
  clearErrors,
  clearSelectedUser,
  setUsersPage,
  setUsersRowsPerPage,
  setVideoPurchasesPage,
  setVideoPurchasesRowsPerPage,
  setFootageHoursPage,
  setFootageHoursRowsPerPage
} = adminUsersSlice.actions;

export default adminUsersSlice.reducer;
